import Game from "../Game";
import {
  SPRITESHEET_KEYS,
  SPRITESHEET_FRAMES,
} from "../../consts/SPRITESHEETS";

export default class FormationPointer {
  private scene: Phaser.Scene;
  private arrows: Phaser.GameObjects.Image[] = [];
  private arrowPulses: Phaser.GameObjects.Image[] = [];
  private readonly ARROW_DISTANCE = 330;
  private readonly VISIBILITY_THRESHOLD = 350; // Distance in pixels when pointer should hide
  private readonly ARROW_COLORS = [
    0xffff00, 0xff0000, 0x00ff00, 0x0000ff, 0xff00ff,
  ]; // Yellow, Red, Green, Blue, Magenta
  private readonly PULSE_INTERVAL = 5000; // Time in ms between pulses
  private readonly PULSE_DURATION = 800; // Duration of each pulse animation

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    // We'll create arrows dynamically as needed
  }

  update() {
    const currentMinute = Math.floor(Game.Core.SurvivedSeconds / 60);

    if (Game.Core.formationManager.formations.length === 0) {
      this.hideAllArrows();
      return;
    }

    // Make sure we have enough arrows
    this.ensureArrows(Game.Core.formationManager.formations.length);

    // Update each arrow for each formation
    Game.Core.formationManager.formations.forEach((formation, index) => {
      const config = formation?.getConfig();

      // Check if this formation should show a pointer yet
      if (
        config?.showPointerAfterMinute !== undefined &&
        currentMinute < config.showPointerAfterMinute
      ) {
        this.arrows[index].setVisible(false);
        this.arrowPulses[index].setVisible(false);
        return;
      }

      const player = Game.Core.Player;
      const distance = Phaser.Math.Distance.Between(
        player.x,
        player.y,
        formation.x,
        formation.y
      );

      // Hide pointer if formation is close enough
      if (distance < this.VISIBILITY_THRESHOLD) {
        this.arrows[index].setVisible(false);
        this.arrowPulses[index].setVisible(false);
        return;
      }

      const angle = Phaser.Math.Angle.Between(
        player.x,
        player.y,
        formation.x,
        formation.y
      );

      const screenCenterX = this.scene.cameras.main.width / 2;
      const screenCenterY = this.scene.cameras.main.height / 2;

      const arrowX = screenCenterX + Math.cos(angle) * this.ARROW_DISTANCE;
      const arrowY = screenCenterY + Math.sin(angle) * this.ARROW_DISTANCE;

      this.arrows[index].setPosition(arrowX, arrowY);
      this.arrows[index].setRotation(angle + Phaser.Math.DegToRad(90));
      this.arrows[index].setVisible(true);

      // Update pulse position to match arrow
      this.arrowPulses[index].setPosition(arrowX, arrowY);
      this.arrowPulses[index].setRotation(angle + Phaser.Math.DegToRad(90));
      this.arrowPulses[index].setVisible(true);
    });

    // Hide any unused arrows
    for (
      let i = Game.Core.formationManager.formations.length;
      i < this.arrows.length;
      i++
    ) {
      this.arrows[i].setVisible(false);
      this.arrowPulses[i].setVisible(false);
    }
  }

  private ensureArrows(count: number) {
    // Create new arrows if needed
    while (this.arrows.length < count) {
      const arrowIndex = this.arrows.length;
      const colorIndex = arrowIndex % this.ARROW_COLORS.length;
      const color = this.ARROW_COLORS[colorIndex];

      // Create the main arrow - using spritesheet
      const arrow = this.scene.add
        .image(0, 0, SPRITESHEET_KEYS.WEAPONS, SPRITESHEET_FRAMES.WEAPONS.ARROW)
        .setAlpha(0.6)
        .setScale(2)
        .setDepth(1000)
        .setScrollFactor(0)
        .setVisible(false)
        .setRotation(Phaser.Math.DegToRad(90));

      arrow.setTint(color);
      this.arrows.push(arrow);

      // Create the pulsing effect arrow - using spritesheet
      const pulse = this.scene.add
        .image(0, 0, SPRITESHEET_KEYS.WEAPONS, SPRITESHEET_FRAMES.WEAPONS.ARROW)
        .setAlpha(0)
        .setScale(2)
        .setDepth(999) // Just below the main arrow
        .setScrollFactor(0)
        .setVisible(false)
        .setRotation(Phaser.Math.DegToRad(90));

      pulse.setTint(color);
      this.arrowPulses.push(pulse);

      // Create the pulsing animation
      this.createPulseAnimation(arrowIndex);
    }
  }

  private createPulseAnimation(arrowIndex: number) {
    const pulse = this.arrowPulses[arrowIndex];

    // Create a repeating pulse animation
    this.scene.tweens.add({
      targets: pulse,
      alpha: { from: 0.5, to: 0 },
      scale: { from: 2, to: 3.5 },
      duration: this.PULSE_DURATION,
      ease: "Sine.easeOut",
      repeat: -1,
      repeatDelay: this.PULSE_INTERVAL - this.PULSE_DURATION,
      delay: arrowIndex * 300, // Stagger the pulses for different arrows
      onRepeat: () => {
        // Ensure the main arrow maintains its correct alpha
        this.arrows[arrowIndex].setAlpha(0.6);
      },
      onUpdate: () => {
        // Ensure the main arrow maintains its correct alpha during animation
        this.arrows[arrowIndex].setAlpha(0.6);
      },
    });
  }

  private hideAllArrows() {
    this.arrows.forEach((arrow) => arrow.setVisible(false));
    this.arrowPulses.forEach((pulse) => pulse.setVisible(false));
  }

  destroy() {
    this.arrows.forEach((arrow) => arrow.destroy());
    this.arrowPulses.forEach((pulse) => {
      // Stop any active tweens on the pulse
      this.scene.tweens.killTweensOf(pulse);
      pulse.destroy();
    });
    this.arrows = [];
    this.arrowPulses = [];
  }
}
